import { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext(null);
export const useLang = () => useContext(LangContext);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  return (
    <LangContext.Provider value={{ lang, setLang, t: dict[lang] }}>
      {children}
    </LangContext.Provider>
  );
};

const dict = {
  en: {
    nav: {
      process: "Process",
      work: "Our Work",
      wholesalers: "For Wholesalers",
      contact: "Contact",
      about: "About",
      quote: "Request a Quote",
      switchTo: "عربي",
    },
    about: {
      overline: "Our story",
      h1a: "A factory built on",
      h1b: "kept promises.",
      p1: "Backed by HHB Group's 25 years in the garment trade, HHB Clothing Co started with a simple frustration: great product ideas were dying between five different vendors. So we built one house that does it all — patterns, sourcing, sampling, production, quality control, branding and packing.",
      p2: "Today we develop and produce garments for wholesalers and bulk buyers across categories — from kaftans and abayas to hoodies, shirting and activewear. Every quote carries a delivery date in writing, and every batch passes a 100% final inspection before it ships.",
      values: [
        { t: "One roof", b: "Every stage under one accountable team — no chasing vendors." },
        { t: "Honest timelines", b: "We quote dates we can keep, then keep them." },
        { t: "Craft first", b: "Finishing, fit and fabric hand-feel are checked by people who care." },
      ],
      cta: "Start a project with us",
    },
    faq: {
      overline: "Questions, answered",
      h1a: "Everything buyers",
      h1b: "ask us first.",
      items: [
        { q: "What is your minimum order quantity?", a: "Minimums are flexible and confirmed with your written quote — they depend on the garment, fabric and decoration. We size them to your stage, whether you are launching or scaling." },
        { q: "How long does an order take?", a: "You receive a quotation within 48 hours. Approval samples take about 10–12 days. Bulk production timing is scheduled in your quote — and the delivery date is a promise, not an estimate." },
        { q: "How much does sampling cost?", a: "Sampling is charged per style and fully deducted from your first bulk order. The sample is yours to keep and test." },
        { q: "What are your payment terms?", a: "A 50% deposit starts production and the balance is due before dispatch. Terms are confirmed in writing with every quote." },
        { q: "Do you ship internationally?", a: "Yes — we pack to your specification and ship worldwide by sea, air or express courier. Shipping terms are agreed per order." },
        { q: "Can you make custom labels and packaging?", a: "Yes — woven labels, care labels, hang tags, polybags and cartons, all produced to your brand specification and applied before dispatch." },
      ],
      cta: "Still curious? Ask us directly",
    },
    contact: {
      overline: "Contact",
      h1a: "Talk to the",
      h1b: "factory.",
      sub: "Call, email or chat — a production specialist answers, not a bot.",
      emailL: "Email", phoneL: "Phone / WhatsApp",
      addressL: "Visit us",
      addressV: "1701, 1703, Block A, Shuguang Mansion, Yiwu City, Zhejiang, China",
      directionsCta: "Get directions",
      quoteCta: "Prefer a written brief? Request a quote",
    },
    hero: {
      overline: "Garment development & production partner",
      h1a: "From idea to packed product,",
      h1b: "we build it for you.",
      sub: "HHB Clothing Co develops and produces garments for wholesalers and bulk buyers — tech packs, sourcing, sampling, production and packing under one roof. Send an idea, receive a finished product.",
      cta1: "Request a Quote",
      cta2: "WhatsApp Us",
      stats: [
        { v: "48h", l: "quote turnaround" },
        { v: "25+", l: "years in the trade" },
        { v: "7", l: "garment categories" },
        { v: "100%", l: "final inspection" },
      ],
    },
    marquee: ["T-Shirts", "Hoodies", "Sweatpants", "Caps & Hats", "Moroccan Gowns", "Ladies Hijab", "Yoga Sets", "Custom Designs"],
    rail: {
      overline: "Off our production line",
      h2a: "Every hanger",
      h2b: "is a client's product.",
      sub: "Hover or tap a garment to see it up close — each piece was developed and produced by us for someone like you.",
      view: "View this project",
      items: [
        { name: "Floral evening gown", client: "Made for a boutique retail brand", brief: "An occasion gown developed from two reference photos into a production-ready style.", handled: ["Design", "Sourcing", "Production", "Packing"], result: "300 pcs delivered, re-ordered twice." },
        { name: "Emerald kaftan", client: "Made for a Moroccan fashion house", brief: "A traditional kaftan with modern trim, graded for export sizing.", handled: ["Sourcing", "Production", "Branding", "Packing"], result: "Four colourways across three consistent batches." },
        { name: "Paisley hijab set", client: "Made for a modest-fashion label", brief: "Lightweight printed hijab with rolled hems and a branded belly band.", handled: ["Sourcing", "Production", "Branding"], result: "2,000 pcs across six prints, zero QC returns." },
        { name: "Rose yoga set", client: "Made for an activewear start-up", brief: "A two-piece set with a tested squat-proof fabric requirement.", handled: ["Design", "Sourcing", "Production"], result: "Launch stock ready in six weeks." },
        { name: "Crimson city jacket", client: "Made for a streetwear wholesaler", brief: "A statement jacket with custom hardware and lining at programme volume.", handled: ["Sourcing", "Production", "Packing"], result: "Three styles running with mixed-size cartons." },
        { name: "Everyday hoodie", client: "Made for a fashion retailer", brief: "A heavyweight fleece hoodie with tonal drawcords and a retail-ready finish.", handled: ["Design", "Production", "Packing"], result: "Now a core style with monthly re-runs." },
        { name: "Crisp poplin shirt", client: "Made for a corporate supplier", brief: "An easy-care white shirt with consistent collar construction at volume.", handled: ["Production", "Packing"], result: "A 10,000 pcs annual programme in monthly drops." },
      ],
    },
    hats: {
      overline: "The hat cupboard",
      h2a: "Caps & hats,",
      h2b: "on the shelf.",
      sub: "Headwear we produce — hover or tap a hat to see its name. Custom shapes, colours and embroidery on request.",
      items: ["Sand baseball cap", "Black snapback", "Khaki bucket hat", "Charcoal beanie", "Straw boater", "Brown logo snapback", "Forest green cap", "Red corduroy cap"],
    },
    process: {
      overline: "What we do for you",
      h2a: "From scratch to packing,",
      h2b: "under one roof.",
      sub: "Six services, eight stages, one accountable partner — backed by HHB Group's 25 years in the garment trade. You approve every stage before we move an inch of fabric.",
      caps: [
        { t: "Product development", b: "Idea to a defined product — silhouettes, sizes, colours, costs." },
        { t: "Patterns & samples", b: "Your brief becomes a garment you can approve." },
        { t: "Fabric & trims", b: "Mill-direct, tested for feel, shrinkage and colour." },
        { t: "Bulk production", b: "Cutting, stitching, print and embroidery at scale." },
        { t: "Labels & tags", b: "Woven labels, hang tags and heat transfers." },
        { t: "Custom packaging", b: "Polybags, boxes and cartons around your brand." },
      ],
      promise: "Delivery dates are written into every quote — and we don't let clients down.",
      steps: [
        { t: "Idea & design brief", b: "Send a sketch, a sample or even a voice note. We shape it into a clear brief together.", time: "Day one" },
        { t: "Tech pack & pattern making", b: "Measurements, graded sizes and construction details become a factory-ready tech pack.", time: "2–4 days" },
        { t: "Fabric & trims sourcing", b: "We source and test fabrics, threads, zips and trims to hit your quality and price point.", time: "3–7 days" },
        { t: "Sampling & approval", b: "A physical sample lands in your hands. Nothing goes to bulk before you sign it off.", time: "10–12 days" },
        { t: "Production", b: "Your approved sample goes on the line — cutting, stitching, printing and embroidery in-house.", time: "Scheduled at quote" },
        { t: "Quality control", b: "In-line and final inspection on every batch. Faults are caught here, not at your door.", time: "Continuous" },
        { t: "Labelling, tags & packaging", b: "Your labels, hang tags, polybags and cartons — packed exactly to your spec.", time: "Final 2–3 days" },
        { t: "Shipping & delivery", b: "Dispatched on the date we promise. Our delivery record is something we guard fiercely.", time: "On the promised date" },
      ],
    },
    seo: {
      home: { title: "HHB Clothing Co — Garment Manufacturing Partner", description: "HHB Clothing Co develops and produces garments for wholesalers and bulk buyers — tech packs, sourcing, sampling, production and packing under one roof." },
      about: { title: "About Us — HHB Clothing Co", description: "The story, values and factory behind HHB Clothing Co — a garment development and production partner for wholesalers and bulk buyers." },
      faq: { title: "FAQ — HHB Clothing Co", description: "Minimums, sampling, payment, shipping and labelling — answers to the questions wholesale buyers ask HHB Clothing Co most." },
      contact: { title: "Contact — HHB Clothing Co", description: "Reach the HHB Clothing Co factory team directly by email, phone, WhatsApp or WeChat to discuss your bulk production order." },
    },
    paths: {
      overline: "Who we work with",
      h2a: "Built for wholesalers.",
      h2b: "Relied on for volume.",
      sub: "We work with bulk buyers only — same factory, same obsession with delivery.",
      card: {
        title: "For Wholesalers",
        sub: "Reliable volume, batch after batch.",
        points: [
          "Mixed-style programmes across our seven garment categories",
          "Consistent quality control across every batch and re-run",
          "Sharp unit economics from mill-direct fabric sourcing",
          "Dispatch on the agreed date — we don't let clients down",
        ],
        moqL: "Minimums", moqV: "Tailored at quote",
        leadL: "Lead time", leadV: "Locked in writing",
        cta: "Discuss a programme",
      },
    },
    portfolio: {
      overline: "Our work",
      h2a: "Products we're proud",
      h2b: "to put our name behind.",
      sub: "A selection of recent programmes. Tap any piece for the full story.",
      filters: ["All", "Gowns", "Hijab", "Activewear", "Outerwear", "Shirting", "Mixed"],
      view: "View project",
      items: [
        { name: "Floral evening gown", cat: "Gowns", client: "Boutique retail brand", brief: "A capsule of occasion gowns with custom floral placement, developed from two reference photos.", handled: ["Design", "Sourcing", "Production", "Packing"], result: "300 pcs delivered in five weeks, re-ordered twice." },
        { name: "Emerald kaftan", cat: "Gowns", client: "Moroccan fashion house", brief: "A traditional kaftan line with modern trim and sizing for export markets.", handled: ["Sourcing", "Production", "Branding", "Packing"], result: "Four colourways, consistent sizing across three batches." },
        { name: "Paisley hijab edit", cat: "Hijab", client: "Modest-fashion label", brief: "Lightweight printed hijabs with rolled hems and branded belly bands.", handled: ["Sourcing", "Production", "Branding"], result: "2,000 pcs across six prints, zero QC returns." },
        { name: "Rose yoga set", cat: "Activewear", client: "Activewear start-up", brief: "A two-piece seamless-look set with a squat-proof fabric requirement.", handled: ["Design", "Sourcing", "Production"], result: "Fabric passed testing first round; launch stock in six weeks." },
        { name: "Crimson city jacket", cat: "Outerwear", client: "Streetwear wholesaler", brief: "A statement jacket programme with custom hardware and lining.", handled: ["Sourcing", "Production", "Packing"], result: "Three styles running, mixed-size cartons per store." },
        { name: "Crisp poplin shirt", cat: "Shirting", client: "Corporate supplier", brief: "An easy-care white shirt with consistent collar construction at volume.", handled: ["Production", "Packing"], result: "A 10,000 pcs annual programme with monthly drops." },
        { name: "Studio active set", cat: "Activewear", client: "Gym-wear retailer", brief: "A branded training range with heat-transfer logos.", handled: ["Design", "Production", "Branding"], result: "Full size range XS–3XL, graded in-house." },
        { name: "Retail starter programme", cat: "Mixed", client: "New retail chain", brief: "One supplier for a mixed opening range: tees, hoodies and caps.", handled: ["Sourcing", "Production", "Branding", "Packing"], result: "A single shelf-ready delivery of twelve styles." },
      ],
    },
    trust: {
      overline: "Why HHB",
      h2a: "Trusted because",
      h2b: "we deliver.",
      why: [
        { t: "Quality control at every stage", b: "In-line and final inspection — faults are caught on our floor, never on yours." },
        { t: "Delivery dates we actually keep", b: "Your dispatch date is written into the quote. We treat it as a promise, not an estimate." },
        { t: "Flexible quantities, honest advice", b: "Minimums tailored to your stage — we would rather grow with you than oversell you." },
        { t: "One partner, sketch to carton", b: "No chasing five vendors. One team owns your product from brief to packed box." },
      ],
      stats: [
        { v: "48h", l: "quote turnaround" },
        { v: "25+", l: "years of experience" },
        { v: "7", l: "garment categories" },
        { v: "100%", l: "final inspection" },
      ],
      quotes: [
        { text: "We sent one photo of a hoodie and had a production sample in under two weeks. The bulk run matched it stitch for stitch.", name: "Lena Hoffmann", role: "Founder, Nord & Co." },
        { text: "Their kaftan line is the best finishing we have sourced anywhere. Embroidery, buttons, packaging — all to spec.", name: "Yassine El Amrani", role: "Buyer, Kasbah Supply" },
        { text: "Quotes in 48 hours, honest timelines, and quality control that actually catches things. Reordering is a one-message job.", name: "Priya Shah", role: "Head of Product, Vela Sport" },
      ],
    },
    chat: {
      overline: "Scan & chat",
      h2a: "Straight to the",
      h2b: "factory floor.",
      sub: "Prefer chat? Scan a code with your phone camera — or tap a card to open the conversation on this device. Send us a sample photo and we take it from there.",
      open: "Open chat",
      whatsappHandle: "HHB Clothing",
      wechatHandle: "HHB Clothing",
    },
    quote: {
      overline: "Get a quote",
      h2a: "Tell us what you want",
      h2b: "to make.",
      sub: "Share your product type, quantity and deadline. A production specialist replies within 48 hours with pricing, minimums and a sampling plan.",
      note: "Minimums and lead times are confirmed with your written quote — and our delivery dates are promises, not estimates.",
      files: "Attach sketches, photos or a tech pack",
      filesHint: "JPG, PNG, WEBP or PDF · up to 5 files · 10 MB each",
      fileTooBig: "is over 10 MB",
      fileBadType: "is not an allowed file type",
      emailL: "Email", phoneL: "Phone", chatCta: "Faster on WhatsApp",
      labels: { name: "Your name *", business: "Business / brand", email: "Email *", phone: "Phone / WhatsApp *", type: "Product type *", qty: "Estimated quantity *", timeline: "Target deadline", message: "About your order" },
      placeholders: { name: "Amira Bennis", business: "Atlas Retail Co.", email: "you@brand.com", phone: "+86 157 5799 2199", qty: "e.g. 500 pieces", timeline: "e.g. delivery needed by October", message: "Fabric, colours, sizes, printing or embroidery — anything that helps us quote accurately." },
      types: ["T-Shirts", "Hoodies & Sweatshirts", "Sweatpants & Joggers", "Caps & Hats", "Moroccan Gowns / Kaftans", "Ladies Hijab", "Yoga & Activewear", "Other / Custom design"],
      submit: "Send enquiry",
      sending: "Sending…",
      success: "Enquiry received — we reply within 48 hours.",
      error: "Something went wrong sending your enquiry. Please try WhatsApp instead.",
    },
    footer: {
      tag1: "Let's build your",
      tag2: "next product.",
      cta: "Request a Quote",
      contactL: "Contact", exploreL: "Explore", chatL: "Chat",
      waText: "WhatsApp the factory",
      rights: "© 2026 HHB Clothing Co. From idea to packed product.",
    },
    modal: { briefL: "The brief", handledL: "What we handled", resultL: "The result", clientL: "Client", close: "Close" },
    float: { aria: "Chat with HHB Clothing Co on WhatsApp" },
  },

  ar: {
    nav: {
      process: "خطوات العمل",
      work: "أعمالنا",
      wholesalers: "لتجار الجملة",
      contact: "تواصل معنا",
      about: "من نحن",
      quote: "اطلب عرض سعر",
      switchTo: "EN",
    },
    about: {
      overline: "قصتنا",
      h1a: "مصنع بُني على",
      h1b: "وعود تُوفى.",
      p1: "بدعم من خبرة HHB Group الممتدة 25 عاماً في مجال الملابس، بدأت HHB Clothing Co من ملاحظة بسيطة: أفكار رائعة كانت تضيع بين خمسة موردين مختلفين. فبنينا داراً واحدة تفعل كل شيء — الباترون والتوريد والعينات والإنتاج ومراقبة الجودة والعلامة والتغليف.",
      p2: "اليوم نطوّر وننتج الملابس لتجار الجملة والطلبات الكبيرة عبر فئات متعددة — من القفاطين والعبايات إلى الهوديز والقمصان والملابس الرياضية. كل عرض سعر يحمل موعد تسليم مكتوباً، وكل دفعة تمر بفحص نهائي شامل قبل الشحن.",
      values: [
        { t: "سقف واحد", b: "كل مرحلة تحت فريق واحد مسؤول — لا ملاحقة للموردين." },
        { t: "مواعيد صادقة", b: "نحدد مواعيد نستطيع الالتزام بها، ثم نلتزم بها." },
        { t: "الحرفة أولاً", b: "التشطيب والمقاس وملمس القماش يفحصها أناس يهتمون." },
      ],
      cta: "ابدأ مشروعاً معنا",
    },
    faq: {
      overline: "أسئلة وأجوبة",
      h1a: "كل ما يسألنا عنه",
      h1b: "المشترون أولاً.",
      items: [
        { q: "ما هو الحد الأدنى لكمية الطلب؟", a: "الحدود الدنيا مرنة وتُؤكد في عرض السعر المكتوب — وتعتمد على نوع الملابس والقماش والزخرفة. نحددها بما يناسب مرحلتك، سواء كنت تطلق علامة جديدة أو تتوسع." },
        { q: "كم يستغرق تنفيذ الطلب؟", a: "تتلقى عرض السعر خلال 48 ساعة، والعينات تحتاج نحو 10–12 يوماً. مدة الإنتاج بالجملة تُحدد في عرض السعر — وموعد التسليم وعد وليس تقديراً." },
        { q: "كم تكلفة العينة؟", a: "تُحتسب العينة لكل موديل وتُخصم كاملة من أول طلب جملة. العينة ملكك لتختبرها." },
        { q: "ما هي شروط الدفع؟", a: "دفعة أولى 50% لبدء الإنتاج، والرصيد المتبقي قبل الشحن. تُؤكد الشروط كتابةً مع كل عرض سعر." },
        { q: "هل تشحنون دولياً؟", a: "نعم — نعبئ حسب مواصفاتك ونشحن لجميع أنحاء العالم بحراً أو جواً أو بالبريد السريع، وتُتفق شروط الشحن لكل طلب." },
        { q: "هل تصنعون ملصقات وتغليفاً خاصاً؟", a: "نعم — ملصقات منسوجة وبطاقات عناية وهانغ تاغ وأكياس وكراتين، كلها حسب مواصفات علامتك وتُركّب قبل الشحن." },
      ],
      cta: "ما زلت تتساءل؟ اسألنا مباشرة",
    },
    contact: {
      overline: "تواصل معنا",
      h1a: "تحدث مع",
      h1b: "المصنع.",
      sub: "اتصل أو راسلنا — يرد عليك مختص إنتاج، وليس روبوت.",
      emailL: "البريد الإلكتروني", phoneL: "الهاتف / واتساب",
      addressL: "زورونا",
      addressV: "1701، 1703، المبنى A، برج شوغوانغ، مدينة ييوو، مقاطعة تشجيانغ، الصين",
      directionsCta: "احصل على الاتجاهات",
      quoteCta: "تفضل موجزاً مكتوباً؟ اطلب عرض سعر",
    },
    hero: {
      overline: "شريك تطوير وإنتاج الملابس",
      h1a: "من الفكرة إلى المنتج المُغلَّف،",
      h1b: "نصنعه لك.",
      sub: "HHB Clothing Co تطوّر وتنتج الملابس لتجار الجملة والطلبات الكبيرة — من الحزمة الفنية والتوريد إلى العينات والإنتاج والتغليف تحت سقف واحد. أرسل فكرتك، واستلم منتجاً جاهزاً.",
      cta1: "اطلب عرض سعر",
      cta2: "راسلنا واتساب",
      stats: [
        { v: "48", l: "ساعة لعرض السعر" },
        { v: "+25", l: "عاماً من الخبرة" },
        { v: "7", l: "فئات من الملابس" },
        { v: "100%", l: "فحص نهائي شامل" },
      ],
    },
    marquee: ["قمصان", "هوديز", "بناطيل رياضية", "قبعات", "قفاطين مغربية", "حجاب نسائي", "أطقم يوغا", "تصاميم خاصة"],
    rail: {
      overline: "من خط إنتاجنا",
      h2a: "كل علاقة",
      h2b: "تحمل منتج عميل.",
      sub: "مرّر المؤشر أو اضغط على أي قطعة لتراها عن قرب — كل قطعة طوّرناها وأنتجناها لعميل مثلك.",
      view: "عرض هذا المشروع",
      items: [
        { name: "فستان سهرة مزهر", client: "صُنع لعلامة بوتيك تجزئة", brief: "فستان سهرة طُوّر من صورتين مرجعيتين إلى موديل جاهز للإنتاج.", handled: ["التصميم", "التوريد", "الإنتاج", "التغليف"], result: "300 قطعة سُلّمت، وأُعيد الطلب مرتين." },
        { name: "قفطان زمردي", client: "صُنع لدار أزياء مغربية", brief: "قفطان تقليدي بلمسة عصرية ومقاسات مدرّجة للتصدير.", handled: ["التوريد", "الإنتاج", "العلامة", "التغليف"], result: "أربعة ألوان عبر ثلاث دفعات متطابقة." },
        { name: "طقم حجاب بيزلي", client: "صُنع لعلامة أزياء محتشمة", brief: "حجاب خفيف مطبوع بحواف ملفوفة وشريط يحمل العلامة.", handled: ["التوريد", "الإنتاج", "العلامة"], result: "2000 قطعة بست طبعات، ولا مرتجعات جودة." },
        { name: "طقم يوغا وردي", client: "صُنع لعلامة رياضية ناشئة", brief: "طقم من قطعتين بقماش مختبر غير شفاف.", handled: ["التصميم", "التوريد", "الإنتاج"], result: "مخزون الإطلاق جاهز خلال ستة أسابيع." },
        { name: "جاكيت مدينة قرمزي", client: "صُنع لتاجر جملة ستريت وير", brief: "جاكيت مميز بإكسسوارات وبطانة خاصة بكميات برنامج كاملة.", handled: ["التوريد", "الإنتاج", "التغليف"], result: "ثلاثة موديلات مستمرة بكراتين مقاسات مختلطة." },
        { name: "هودي يومي", client: "صُنع لتاجر أزياء", brief: "هودي فليس ثقيل بحبال بلون القماش وتشطيب جاهز للمتاجر.", handled: ["التصميم", "الإنتاج", "التغليف"], result: "أصبح موديلاً أساسياً بإعادة طلب شهرية." },
        { name: "قميص بوبلين أنيق", client: "صُنع لمورّد زي موحد", brief: "قميص أبيض سهل العناية بياقة متسقة البناء بكميات كبيرة.", handled: ["الإنتاج", "التغليف"], result: "برنامج سنوي 10,000 قطعة بدفعات شهرية." },
      ],
    },
    hats: {
      overline: "خزانة القبعات",
      h2a: "قبعات بأشكالها،",
      h2b: "على الرف.",
      sub: "أغطية الرأس التي ننتجها — مرّر المؤشر أو اضغط على أي قبعة لترى اسمها. أشكال وألوان وتطريز خاص عند الطلب.",
      items: ["قبعة بيسبول رملية", "سناباك سوداء", "قبعة باكيت كاكي", "بيني فحمية", "قبعة قش كلاسيكية", "سناباك بنية بشعار", "قبعة خضراء غامقة", "قبعة كوردروي حمراء"],
    },
    process: {
      overline: "ماذا نقدم لك",
      h2a: "من الصفر إلى التغليف،",
      h2b: "تحت سقف واحد.",
      sub: "ست خدمات وثماني مراحل وشريك واحد مسؤول — بخبرة HHB Group الممتدة 25 عاماً في مجال الملابس. أنت تعتمد كل مرحلة قبل أن نحرك متراً من القماش.",
      caps: [
        { t: "تطوير المنتجات", b: "من الفكرة إلى منتج محدد — قصّات ومقاسات وألوان وتكلفة." },
        { t: "الباترون والعينات", b: "موجزك يصبح قطعة يمكنك اعتمادها." },
        { t: "الأقمشة والإكسسوارات", b: "من المنسوجات مباشرة، مختبرة للملمس والانكماش واللون." },
        { t: "الإنتاج بالجملة", b: "قص وخياطة وطباعة وتطريز بكميات كبيرة." },
        { t: "الملصقات والبطاقات", b: "ملصقات منسوجة وهانغ تاغ وطباعة حرارية." },
        { t: "التغليف الخاص", b: "أكياس وعلب وكراتين حول علامتك." },
      ],
      promise: "مواعيد التسليم تُكتب في كل عرض سعر — ونحن لا نخذل عملاءنا.",
      steps: [
        { t: "الفكرة وموجز التصميم", b: "أرسل رسمة أو عينة أو حتى رسالة صوتية، ونحوّلها معاً إلى موجز واضح.", time: "من اليوم الأول" },
        { t: "الحزمة الفنية والباترون", b: "المقاسات والأحجام المدرّجة وتفاصيل التصنيع تصبح حزمة فنية جاهزة للمصنع.", time: "2–4 أيام" },
        { t: "توريد الأقمشة والإكسسوارات", b: "نورد ونختبر الأقمشة والخيوط والسحابات بما يحقق جودتك وسعرك المستهدف.", time: "3–7 أيام" },
        { t: "العينات والاعتماد", b: "عينة حقيقية تصل بين يديك، ولا ينتقل شيء للإنتاج بالجملة قبل توقيعك.", time: "10–12 يوماً" },
        { t: "الإنتاج", b: "عينتك المعتمدة على الخط — قص وخياطة وطباعة وتطريز داخلياً.", time: "يُحدد في عرض السعر" },
        { t: "مراقبة الجودة", b: "فحص أثناء الإنتاج وفحص نهائي لكل دفعة — الأخطاء تُكتشف عندنا لا عندك.", time: "مستمرة" },
        { t: "الملصقات والتغليف", b: "ملصقاتك وبطاقاتك وأكياسك وكراتينك — تُعبأ حسب مواصفاتك تماماً.", time: "آخر 2–3 أيام" },
        { t: "الشحن والتسليم", b: "الإرسال في الموعد الذي وعدنا به — سجل التسليم لدينا خط أحمر.", time: "في الموعد الموعود" },
      ],
    },
    seo: {
      home: { title: "HHB Clothing Co — شريك تصنيع الملابس", description: "HHB Clothing Co تطوّر وتنتج الملابس لتجار الجملة والطلبات الكبيرة — من الحزمة الفنية والتوريد إلى العينات والإنتاج والتغليف تحت سقف واحد." },
      about: { title: "من نحن — HHB Clothing Co", description: "قصة HHB Clothing Co وقيمها ومصنعها — شريك تطوير وإنتاج الملابس لتجار الجملة والطلبات الكبيرة." },
      faq: { title: "الأسئلة الشائعة — HHB Clothing Co", description: "الحد الأدنى للطلب والعينات والدفع والشحن والملصقات — إجابات عن أكثر أسئلة تجار الجملة تكراراً." },
      contact: { title: "اتصل بنا — HHB Clothing Co", description: "تواصل مباشرة مع فريق مصنع HHB Clothing Co عبر البريد أو الهاتف أو واتساب أو ويتشات لمناقشة طلب الإنتاج بالجملة." },
    },
    paths: {
      overline: "مع من نعمل",
      h2a: "صُمم لتجار الجملة.",
      h2b: "موثوق للكميات الكبيرة.",
      sub: "نتعامل مع تجار الجملة فقط — نفس المصنع ونفس الهوس بالتسليم.",
      card: {
        title: "لتجار الجملة",
        sub: "كميات موثوقة، دفعة بعد دفعة.",
        points: [
          "برامج متنوعة عبر سبع فئات من الملابس",
          "جودة ثابتة في كل دفعة وكل إعادة طلب",
          "أسعار تنافسية بفضل التوريد المباشر من المنسوجات",
          "شحن في الموعد المتفق عليه — لا نخذل عملاءنا",
        ],
        moqL: "الحد الأدنى", moqV: "يُحدد في عرض السعر",
        leadL: "مدة التسليم", leadV: "تُثبت كتابةً",
        cta: "ناقش برنامجاً",
      },
    },
    portfolio: {
      overline: "أعمالنا",
      h2a: "منتجات نفخر",
      h2b: "بوضع اسمنا خلفها.",
      sub: "مختارات من برامج حديثة. اضغط أي قطعة لقراءة قصتها كاملة.",
      filters: ["الكل", "قفاطين وفساتين", "حجاب", "رياضة", "جاكيتات", "قمصان", "متنوع"],
      view: "عرض المشروع",
      items: [
        { name: "فستان سهرة مزهر", cat: "قفاطين وفساتين", client: "علامة بوتيك تجزئة", brief: "كبسولة فساتين سهرة بتوزيع زهور خاص، طُوّرت من صورتين مرجعيتين.", handled: ["التصميم", "التوريد", "الإنتاج", "التغليف"], result: "300 قطعة خلال خمسة أسابيع، وأُعيد الطلب مرتين." },
        { name: "قفطان زمردي", cat: "قفاطين وفساتين", client: "دار أزياء مغربية", brief: "خط قفاطين تقليدي بلمسة عصرية ومقاسات مناسبة لأسواق التصدير.", handled: ["التوريد", "الإنتاج", "العلامة", "التغليف"], result: "أربعة ألوان بمقاسات متسقة عبر ثلاث دفعات." },
        { name: "إصدار حجاب بيزلي", cat: "حجاب", client: "علامة أزياء محتشمة", brief: "حجابات خفيفة مطبوعة بحواف ملفوفة وأشرطة تحمل العلامة.", handled: ["التوريد", "الإنتاج", "العلامة"], result: "2000 قطعة بست طبعات ولا مرتجعات جودة." },
        { name: "طقم يوغا وردي", cat: "رياضة", client: "علامة رياضية ناشئة", brief: "طقم من قطعتين بمظهر سلس ومتطلب قماش غير شفاف.", handled: ["التصميم", "التوريد", "الإنتاج"], result: "القماش اجتاز الاختبار من الجولة الأولى، والمخزون خلال ستة أسابيع." },
        { name: "جاكيت مدينة قرمزي", cat: "جاكيتات", client: "تاجر جملة ستريت وير", brief: "برنامج جاكيت مميز بإكسسوارات وبطانة مخصصة.", handled: ["التوريد", "الإنتاج", "التغليف"], result: "ثلاثة موديلات مستمرة بكراتين مختلطة لكل متجر." },
        { name: "قميص بوبلين أنيق", cat: "قمصان", client: "مورّد زي موحد", brief: "قميص أبيض سهل العناية ببناء ياقة متسق بكميات كبيرة.", handled: ["الإنتاج", "التغليف"], result: "برنامج سنوي 10,000 قطعة بدفعات شهرية." },
        { name: "طقم رياضي استوديو", cat: "رياضة", client: "تاجر ملابس رياضية", brief: "مجموعة تدريب بشعارات مطبوعة حرارياً.", handled: ["التصميم", "الإنتاج", "العلامة"], result: "نطاق مقاسات كامل XS–3XL مدرّج داخلياً." },
        { name: "برنامج انطلاق متجر", cat: "متنوع", client: "سلسلة تجزئة جديدة", brief: "مورد واحد لتشكيلة افتتاح متنوعة: قمصان وهوديز وقبعات.", handled: ["التوريد", "الإنتاج", "العلامة", "التغليف"], result: "تسليم واحد جاهز للرفوف باثني عشر موديلاً." },
      ],
    },
    trust: {
      overline: "لماذا HHB",
      h2a: "يثقون بنا لأننا",
      h2b: "نُسلّم.",
      why: [
        { t: "مراقبة جودة في كل مرحلة", b: "فحص أثناء الإنتاج وفحص نهائي — الأخطاء تُكتشف في أرضيتنا لا في أرضيتك." },
        { t: "مواعيد تسليم نلتزم بها فعلاً", b: "تاريخ الإرسال يُكتب في عرض السعر، ونتعامل معه كوعد لا كتقدير." },
        { t: "كميات مرنة ونصيحة صادقة", b: "حدود دنيا تناسب مرحلتك — نفضل أن نكبر معك على أن نبيعك أكثر من حاجتك." },
        { t: "شريك واحد من الرسمة إلى الكرتونة", b: "لا ملاحقة خمسة موردين — فريق واحد يملك منتجك من الموجز إلى الصندوق." },
      ],
      stats: [
        { v: "48", l: "ساعة لعرض السعر" },
        { v: "+25", l: "عاماً من الخبرة" },
        { v: "7", l: "فئات من الملابس" },
        { v: "100%", l: "فحص نهائي" },
      ],
      quotes: [
        { text: "أرسلنا صورة هودي واحدة فوصلتنا عينة إنتاج في أقل من أسبوعين، والدفعة طابقتها غرزة بغرزة.", name: "لينا هوفمان", role: "مؤسِّسة Nord & Co." },
        { text: "خط القفاطين لديهم بأفضل تشطيب حصلنا عليه — التطريز والأزرار والتغليف كلها حسب المواصفات.", name: "ياسين العمراني", role: "مسؤول مشتريات Kasbah Supply" },
        { text: "عرض سعر خلال 48 ساعة ومواعيد صادقة ومراقبة جودة تلتقط الأخطاء فعلاً — إعادة الطلب رسالة واحدة.", name: "بريا شاه", role: "مديرة منتجات Vela Sport" },
      ],
    },
    chat: {
      overline: "امسح وتحدث",
      h2a: "مباشرة إلى",
      h2b: "أرضية المصنع.",
      sub: "تفضل المحادثة؟ امسح الرمز بكاميرا هاتفك أو اضغط البطاقة لفتح المحادثة من هذا الجهاز. أرسل صورة عينتك ونتولى الباقي.",
      open: "افتح المحادثة",
      whatsappHandle: "HHB Clothing",
      wechatHandle: "HHB Clothing",
    },
    quote: {
      overline: "اطلب عرض سعر",
      h2a: "أخبرنا ماذا تريد",
      h2b: "أن تصنع.",
      sub: "شاركنا نوع المنتج والكمية والموعد المستهدف، ويرد عليك مختص إنتاج خلال 48 ساعة بالأسعار والحدود الدنيا وخطة العينات.",
      note: "الحدود الدنيا ومدد التسليم تُؤكد في عرض السعر المكتوب — ومواعيد تسليمنا وعود لا تقديرات.",
      files: "أرفق رسومات أو صوراً أو حزمة فنية",
      filesHint: "JPG أو PNG أو WEBP أو PDF · حتى 5 ملفات · 10 ميغا للملف",
      fileTooBig: "يتجاوز 10 ميغا",
      fileBadType: "نوع ملف غير مسموح",
      emailL: "البريد", phoneL: "الهاتف", chatCta: "أسرع عبر واتساب",
      labels: { name: "اسمك *", business: "الشركة / العلامة", email: "البريد الإلكتروني *", phone: "الهاتف / واتساب *", type: "نوع المنتج *", qty: "الكمية التقديرية *", timeline: "الموعد المستهدف", message: "عن طلبك" },
      placeholders: { name: "أميرة بنيس", business: "شركة أطلس للتجزئة", email: "you@brand.com", phone: "+86 157 5799 2199", qty: "مثال: 500 قطعة", timeline: "مثال: التسليم مطلوب قبل أكتوبر", message: "القماش والألوان والمقاسات والطباعة أو التطريب — أي شيء يساعدنا على التسعير بدقة." },
      types: ["قمصان", "هوديز وسويت شيرت", "بناطيل رياضية", "قبعات", "قفاطين مغربية", "حجاب نسائي", "يوغا ورياضة", "أخرى / تصميم خاص"],
      submit: "أرسل الطلب",
      sending: "جارٍ الإرسال…",
      success: "وصلنا طلبك — نرد خلال 48 ساعة.",
      error: "حدث خطأ أثناء إرسال الطلب. جرّب واتساب من فضلك.",
    },
    footer: {
      tag1: "لنصنع",
      tag2: "منتجك القادم.",
      cta: "اطلب عرض سعر",
      contactL: "تواصل", exploreL: "استكشف", chatL: "محادثة",
      waText: "راسل المصنع واتساب",
      rights: "© 2026 HHB Clothing Co — من الفكرة إلى المنتج المُغلَّف.",
    },
    modal: { briefL: "الموجز", handledL: "ما توليناه", resultL: "النتيجة", clientL: "العميل", close: "إغلاق" },
    float: { aria: "تحدث مع HHB Clothing Co عبر واتساب" },
  },
};
